import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DomainStatusResolver } from './statusResolver';
import { buildResolvedAction } from './buildResolvedAction';
import { useActionHandlers, useAutoInvokeOverride, useSiteInfo, useUiConfig } from './context';
import { usePremiumEssentialsContext } from '@wix/premium-fed-sdk/@premium-essentials';
import { CoreDomainSearchModule } from '@wix/core-domain-search';
import { useDomainSearchStatuses, useSuggestionStatuses } from './components/useStatuses';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { DomainSearchSuiteModule } from '../../domainSearchSuite.module';
export const useStatusActionResolver = () => {
  const {
    useStatus,
    useError,
    setStatus,
    setError
  } = DomainSearchSuiteModule.SDK.domainSearchSuiteStore;
  const status = useStatus();
  const error = useError();
  const [loading, setLoading] = useState(false);
  const {
    httpClient,
    siteId
  } = usePremiumEssentialsContext();
  const submittedDomain = CoreDomainSearchModule.SDK.domainInputStore.useSubmittedDomain();
  const {
    externalViewer
  } = useSiteInfo();
  const {
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab
  } = useUiConfig();
  const {
    data: accountParams
  } = DomainsApiClient.hooks.useDomainsAccount({});
  const domainStatusResolver = useMemo(() => new DomainStatusResolver(httpClient, {
    msid: siteId,
    externalViewer
  }), [httpClient, siteId, externalViewer]);
  const submittedDomainRef = useRef(submittedDomain);
  submittedDomainRef.current = submittedDomain;
  const resolveStatus = useCallback(async _ref => {
    let {
      domainName
    } = _ref;
    setLoading(true);
    setError(undefined);
    try {
      const result = await domainStatusResolver.resolveStatus({
        domainName
      });
      if (submittedDomainRef.current === domainName) {
        setStatus(result == null ? void 0 : result.status);
      }
    } catch (err) {
      if (submittedDomainRef.current === domainName) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error resolving domain status:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [domainStatusResolver, setStatus, setError, setLoading]);
  const resolveActionForDomain = useCallback(async domain => {
    return domainStatusResolver.resolveStatus({
      domainName: domain,
      overrides: {
        purchaseResult: {
          status: 'fulfilled',
          value: {
            canPurchase: true
          }
        }
      }
    });
  }, [domainStatusResolver]);
  useEffect(() => {
    if (submittedDomain) {
      resolveStatus({
        domainName: submittedDomain
      });
    }
  }, [submittedDomain, resolveStatus, status]);
  return {
    status,
    loading,
    resolveActionForDomain,
    domainName: submittedDomain,
    error,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    siteId: accountParams == null ? void 0 : accountParams.msid,
    siteName: accountParams == null ? void 0 : accountParams.siteName
  };
};
export const useDomainAction = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const executeAction = useCallback(async actionHandler => {
    setLoading(true);
    setError(null);
    try {
      if (!actionHandler) {
        return;
      }
      const result = await actionHandler();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error(`Error executing action:`, err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  return {
    executeAction,
    loading,
    error
  };
};
const useActionResolver = _ref2 => {
  let {
    loading,
    status,
    domainName,
    msid,
    siteName,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab
  } = _ref2;
  const action = useDomainAction();
  const statuses = useDomainSearchStatuses();
  const {
    t
  } = DomainSearchSuiteModule.internals.useTranslations();
  const {
    statusesCustomComponents
  } = DomainSearchSuiteModule.SDK.useStatusesCustomComponents();
  const {
    setIsInvokedAction,
    useIsInvokedAction
  } = DomainSearchSuiteModule.SDK.domainSearchSuiteStore;
  const actionHandlers = useActionHandlers();
  const autoInvokeOverride = useAutoInvokeOverride();
  const isInvokedAction = useIsInvokedAction();
  const commonProps = domainName && status ? {
    domainName,
    msid,
    siteName,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    autoInvoke: autoInvokeOverride,
    t
  } : undefined;
  const {
    action: resolvedAction,
    autoInvoke,
    StatusComponent
  } = buildResolvedAction({
    status,
    commonProps,
    statuses,
    actionHandlers,
    statusesCustomComponents
  });
  const actionStatus = resolvedAction !== undefined || StatusComponent ? {
    action: resolvedAction,
    ...(StatusComponent ? {
      StatusComponent
    } : {})
  } : undefined;
  useEffect(() => {
    if (autoInvoke && status && !isInvokedAction) {
      setIsInvokedAction(true);
      action.executeAction(resolvedAction);
    }
  }, [action, status, autoInvoke, resolvedAction, setIsInvokedAction, isInvokedAction]);
  return {
    actionStatus,
    autoInvoke,
    action: () => action.executeAction(actionStatus == null ? void 0 : actionStatus.action),
    status,
    domainName,
    externalViewer,
    loading
  };
};
export const useSearchActionResolver = () => {
  const {
    status,
    domainName,
    siteId,
    siteName,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    loading
  } = useStatusActionResolver();
  return useActionResolver({
    status,
    domainName,
    msid: siteId,
    siteName,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    loading
  });
};
export const useSuggestionActionResolver = () => {
  const {
    executeAction
  } = useDomainAction();
  const {
    resolveActionForDomain,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    siteName,
    siteId
  } = useStatusActionResolver();
  const statuses = useSuggestionStatuses();
  const {
    t
  } = DomainSearchSuiteModule.internals.useTranslations();
  const actionHandlers = useActionHandlers();
  const resolveActionForSuggestion = useCallback(async (domainName, isPremiumDomain) => {
    const {
      status: domainStatus
    } = await resolveActionForDomain(domainName);
    const {
      action: resolvedAction
    } = buildResolvedAction({
      status: domainStatus,
      commonProps: {
        domainName,
        isPremiumDomain,
        msid: siteId,
        siteName,
        externalViewer,
        fullPage,
        useUpgradePaywall,
        referralAdditionalInfo,
        sameTab,
        t
      },
      statuses,
      actionHandlers
    });
    if (!resolvedAction) {
      return {
        action: () => {},
        Status: undefined
      };
    }
    await executeAction(resolvedAction);
    return {
      action: () => {},
      Status: undefined
    };
  }, [resolveActionForDomain, executeAction, statuses, siteId, siteName, externalViewer, fullPage, useUpgradePaywall, referralAdditionalInfo, sameTab, actionHandlers, t]);
  return {
    resolveActionForSuggestion
  };
};
const useResolveDomainActionCore = () => {
  const {
    httpClient,
    siteId
  } = usePremiumEssentialsContext();
  const {
    externalViewer
  } = useSiteInfo();
  const {
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab
  } = useUiConfig();
  const actionHandlers = useActionHandlers();
  const autoInvokeOverride = useAutoInvokeOverride();
  const statuses = useSuggestionStatuses();
  const {
    statusesCustomComponents
  } = DomainSearchSuiteModule.SDK.useStatusesCustomComponents();
  const {
    t
  } = DomainSearchSuiteModule.internals.useTranslations();
  const {
    data: accountParams
  } = DomainsApiClient.hooks.useDomainsAccount({});
  const currentMsid = siteId ?? (accountParams == null ? void 0 : accountParams.msid);
  const currentSiteName = accountParams == null ? void 0 : accountParams.siteName;
  const inputsRef = useRef({
    httpClient,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    actionHandlers,
    autoInvokeOverride,
    statuses,
    statusesCustomComponents,
    t,
    currentMsid,
    currentSiteName
  });
  inputsRef.current = {
    httpClient,
    externalViewer,
    fullPage,
    useUpgradePaywall,
    referralAdditionalInfo,
    sameTab,
    actionHandlers,
    autoInvokeOverride,
    statuses,
    statusesCustomComponents,
    t,
    currentMsid,
    currentSiteName
  };
  return useCallback(async (domainName, options) => {
    var _options$site, _options$site2;
    const {
      httpClient: client,
      externalViewer: external,
      fullPage: isFullPage,
      useUpgradePaywall: paywall,
      referralAdditionalInfo: referral,
      sameTab: selfTab,
      actionHandlers: handlers,
      autoInvokeOverride: autoInvoke,
      statuses: statusMap,
      statusesCustomComponents: customComponents,
      t: translate,
      currentMsid: msidFallback,
      currentSiteName: siteNameFallback
    } = inputsRef.current;
    const msid = (options == null || (_options$site = options.site) == null ? void 0 : _options$site.siteId) ?? msidFallback;
    const siteName = (options == null || (_options$site2 = options.site) == null ? void 0 : _options$site2.siteName) ?? siteNameFallback;
    const resolver = new DomainStatusResolver(client, {
      msid,
      externalViewer: external
    });
    const {
      status
    } = await resolver.resolveStatus({
      domainName
    });
    return buildResolvedAction({
      status,
      commonProps: {
        domainName,
        msid,
        siteName,
        externalViewer: external,
        fullPage: isFullPage,
        useUpgradePaywall: paywall,
        referralAdditionalInfo: referral,
        sameTab: selfTab,
        autoInvoke,
        t: translate
      },
      statuses: statusMap,
      actionHandlers: handlers,
      statusesCustomComponents: customComponents
    });
  }, []);
};
export const useRunDomainAction = () => {
  const {
    executeAction
  } = useDomainAction();
  const resolveDomainAction = useResolveDomainActionCore();
  return useCallback(async (domainName, options) => {
    const {
      status,
      action
    } = await resolveDomainAction(domainName, options);
    if (action) {
      await executeAction(action);
    }
    return {
      status
    };
  }, [resolveDomainAction, executeAction]);
};
export const useDomainActionStatus = (domainName, options) => {
  const resolveDomainAction = useResolveDomainActionCore();
  const {
    executeAction
  } = useDomainAction();
  const {
    setIsInvokedAction,
    useIsInvokedAction
  } = DomainSearchSuiteModule.SDK.domainSearchSuiteStore;
  const isInvokedAction = useIsInvokedAction();
  const [resolved, setResolved] = useState();
  const [loading, setLoading] = useState(false);
  const site = options == null ? void 0 : options.site;
  const siteId = site == null ? void 0 : site.siteId;
  const siteName = site == null ? void 0 : site.siteName;
  useEffect(() => {
    let active = true;
    if (!domainName) {
      setResolved(undefined);
      return;
    }
    setLoading(true);
    resolveDomainAction(domainName, {
      site
    }).then(result => {
      if (active) {
        setResolved(result);
      }
    }).catch(err => {
      if (active) {
        setResolved(undefined);
        console.error('Error resolving domain action:', err);
      }
    }).finally(() => {
      if (active) {
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainName, siteId, siteName, resolveDomainAction]);
  const autoInvoke = resolved == null ? void 0 : resolved.autoInvoke;
  const resolvedAction = resolved == null ? void 0 : resolved.action;
  const status = resolved == null ? void 0 : resolved.status;
  useEffect(() => {
    if (autoInvoke && status && !isInvokedAction) {
      setIsInvokedAction(true);
      executeAction(resolvedAction);
    }
  }, [autoInvoke, status, resolvedAction, isInvokedAction, setIsInvokedAction, executeAction]);
  return {
    status,
    autoInvoke,
    StatusComponent: resolved == null ? void 0 : resolved.StatusComponent,
    action: () => executeAction(resolvedAction),
    loading
  };
};
//# sourceMappingURL=hooks.js.map